'use client';
import { CheckmarkIcon } from '@c/icons';
import LoadingSpinner from '@c/LoadingSpinner';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { AUTH } from '@util/firebase';
import { updateUserDoc } from '@util/firestore/users';
import { formatAuthError } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { FirebaseError } from 'firebase/app';
import { getRedirectResult, UserCredential } from 'firebase/auth';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import UsernameForm from '../UsernameForm';
import PhoneSignupTab from './tabs/PhoneSignupTab';
import EmailInfoSignupTab from './tabs/UserInfoSignupTab';
import WelcomeTab from './tabs/WelcomeTab';

export const ActiveCheckIcon = ({ active }: { active: boolean }) => {
  return (
    <div
      className={`flex h-[1.6rem] w-[1.6rem] items-center justify-center rounded-full p-[0.3rem] ${
        active ? 'bg-[#1E7A1E]' : 'bg-[#7F7F7F]'
      }`}
    >
      <CheckmarkIcon variant="white" height={12} width={12} />
    </div>
  );
};

const SignupForm = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const stepAppliedRef = useRef(false);
  const [handlingRedirect, setHandlingRedirect] = useState(false);
  const redirectHandledRef = useRef(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const { showSuccessToast } = useToastContext();
  const [userInfo, setUserInfo] = useState({
    uid: '',
    name: '',
    isSocial: false,
    phone: '',
    smsConsent: false,
  });
  const [countryCode, setCountryCode] = useState('');
  const searchParams = useSearchParams();
  const router = useRouter();

  const { attribution } = useAuth();

  useEffect(() => {
    if (typeof window !== 'undefined' && !countryCode) {
      setCountryCode(localStorage.getItem('mxlocker_country_code') || 'US');
    }
  }, [countryCode]);

  const paramsTab = searchParams?.get('step');

  useEffect(() => {
    if (!paramsTab) return;
    if (stepAppliedRef.current) return;

    if (!AUTH.currentUser) {
      router.push('/signup');
      return;
    }

    const index = Number(paramsTab);
    if (!Number.isNaN(index)) {
      stepAppliedRef.current = true;
      setTabIndex(index);
    }
  }, [paramsTab, router]);

  // the below code needs to be here instead of EmailInfoSignupTab
  // because the intial state of this page is the phone signup tab
  useEffect(() => {
    if (redirectHandledRef.current) return;
    redirectHandledRef.current = true;

    const handleRedirectSuccess = async (result: UserCredential) => {
      const user = result.user;
      if (user.uid) {
        await updateUserDoc(user.uid, {
          email: user.email ?? '',
          opted_out: sessionStorage.getItem('optOut') === 'true',
        });
        showSuccessToast('Account linked successfully');
        stepAppliedRef.current = true;
        setTabIndex(2);
      }
    };

    const run = async () => {
      const hasAuthParams = sessionStorage.getItem('auth:redirecting');
      if (hasAuthParams) setHandlingRedirect(true);
      try {
        const result = await getRedirectResult(AUTH);
        if (result) {
          if (!hasAuthParams) setHandlingRedirect(true);
          await handleRedirectSuccess(result);
        }
      } catch (error) {
        logError(error);
        setRedirectError(
          error instanceof FirebaseError
            ? formatAuthError(error)
            : 'An error occurred during authentication. Please try again.'
        );
      } finally {
        sessionStorage.removeItem('auth:redirecting');
        setHandlingRedirect(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  if (handlingRedirect) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex w-full items-start justify-center px-[2.4rem] pb-[3.2rem] sm:mt-[2.4rem] sm:p-0">
      {/* form */}
      {/* inputs and buttons */}
      <TabGroup selectedIndex={tabIndex} onChange={setTabIndex}>
        <TabList className="hidden">
          <Tab></Tab>
          <Tab></Tab>
          <Tab></Tab>
          <Tab></Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <PhoneSignupTab
              // handleSocialClick={handleSocialClick}
              setSignupStep={handleTabChange}
              setCreatedUserDoc={setUserInfo}
              returningUser={paramsTab === '0'}
              attribution={attribution ?? undefined}
              // globalSocialLinkError={globalSocialLinkError}
            />
          </TabPanel>
          <TabPanel>
            <EmailInfoSignupTab
              setTabIndex={handleTabChange}
              redirectError={redirectError}
            />
          </TabPanel>
          <TabPanel>
            <UsernameForm
              uid={userInfo.uid}
              isSocial={userInfo.isSocial}
              setTabIndex={handleTabChange}
            />
          </TabPanel>
          <TabPanel>
            <WelcomeTab />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};

export default SignupForm;
